import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const inputCls =
  "w-full rounded-md border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-stone-800 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600/40 transition-colors";

function AutoTextarea({ value, onChange, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = Math.max(80, el.scrollHeight) + "px"; }
  }, [value]);
  return (
    <textarea
      ref={ref}
      data-testid={testid}
      className={inputCls + " grow-textarea leading-relaxed"}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// Compact auto-growing textarea for table cells: wraps long text and grows
// vertically instead of hiding overflow like a single-line input.
function GridText({ value, onChange, disabled, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; }
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      data-testid={testid}
      disabled={disabled}
      className={inputCls + " resize-none overflow-hidden leading-snug min-h-[38px]"}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

const LIKERT = ["Never", "Rarely", "Sometimes", "Often", "Almost always"];

export default function WorksheetRenderer({ ws, data, onChange, readOnly, dashboardData }) {
  const { t } = useTranslation();
  const val = (k) => data?.[k];
  const set = (k, v, immediate) => !readOnly && onChange(k, v, immediate);

  return (
    <div className="space-y-8">
      {ws.blocks.map((b, bi) => {
        // ---- instructions ----
        if (b.type === "instructions")
          return (
            <div key={bi} className="rounded-md border border-[#CAD2C5] bg-[#E8EFE9] px-4 py-3 text-sm text-stone-700 leading-relaxed" data-testid={`instructions-${ws.id}`}>
              {t(b.text)}
            </div>
          );

        // ---- likert scored table ----
        if (b.type === "likert")
          return (
            <div key={bi} data-testid={`likert-${ws.id}`}>
              <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono uppercase tracking-wider text-stone-500">
                {LIKERT.map((l, i) => <span key={i}>{i} = {t(l)}</span>)}
              </div>
              <div className="divide-y divide-[#EFE9DE] rounded-md border border-[#E5DFD3] overflow-hidden">
                {b.statements.map((st, i) => {
                  const cur = val("s" + i);
                  return (
                    <div key={`${st}-${i}`} className="flex flex-col gap-2 px-4 py-3 hover:bg-[#FDFBF7] sm:flex-row sm:items-center">
                      <div className="flex-1 text-sm text-stone-800"><span className="mr-2 font-mono text-stone-400">{i + 1}.</span>{t(st)}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {[0, 1, 2, 3, 4].map((n) => (
                          <button key={n} type="button" disabled={readOnly}
                            data-testid={`${ws.id}-s${i}-opt${n}`}
                            onClick={() => set("s" + i, cur === n ? "" : n, true)}
                            className={`h-9 w-9 rounded-full border text-sm font-medium transition-colors ${cur === n ? "border-emerald-700 bg-emerald-700 text-white" : "border-[#CAD2C5] bg-white text-stone-600 hover:border-emerald-600"}`}>
                            {n}
                          </button>
                        ))}
                        <button type="button" disabled={readOnly}
                          data-testid={`${ws.id}-s${i}-na`}
                          onClick={() => set("s" + i, cur === "na" ? "" : "na", true)}
                          className={`h-9 rounded-full border px-3 text-xs font-medium transition-colors ${cur === "na" ? "border-stone-500 bg-stone-500 text-white" : "border-[#CAD2C5] bg-white text-stone-500 hover:border-stone-400"}`}>
                          N/A
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );

        // ---- checklist ----
        if (b.type === "checklist")
          return (
            <div key={bi}>
              <h4 className="text-base font-semibold text-stone-800">{t(b.title)}</h4>
              {b.note && <p className="mb-3 text-xs text-stone-500">{t(b.note)}</p>}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {b.items.map((it, i) => {
                  const checked = !!val("chk_" + i);
                  return (
                    <div key={`${it}-${i}`} className="rounded-md border border-[#E5DFD3] bg-white px-3 py-2">
                      <label className="flex items-center gap-2 text-sm text-stone-800">
                        <input type="checkbox" disabled={readOnly} checked={checked}
                          data-testid={`${ws.id}-chk${i}`}
                          onChange={(e) => set("chk_" + i, e.target.checked, true)}
                          className="h-4 w-4 accent-emerald-700" />
                        {t(it)}
                      </label>
                      {checked && (
                        <input className={inputCls + " mt-2 text-xs"} placeholder={t("note / frequency / trigger")}
                          data-testid={`${ws.id}-note${i}`}
                          value={val("note_" + i) || ""} disabled={readOnly}
                          onChange={(e) => set("note_" + i, e.target.value)} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );

        // ---- single textarea ----
        if (b.type === "textarea")
          return (
            <div key={bi}>
              <label className="mb-1 block text-sm font-medium text-stone-700">{t(b.label)}</label>
              <AutoTextarea testid={`${ws.id}-${b.key}`} value={val(b.key)} onChange={(v) => set(b.key, v)} />
            </div>
          );

        // ---- single field ----
        if (b.type === "field")
          return (
            <div key={bi} className="max-w-md">
              <label className="mb-1 block text-sm font-medium text-stone-700">{t(b.label)}</label>
              <FieldInput ws={ws} f={b} val={val} set={set} readOnly={readOnly} />
            </div>
          );

        // ---- field grid ----
        if (b.type === "fields")
          return (
            <div key={bi} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {b.items.map((f) => (
                <div key={f.key}>
                  <label className="mb-1 block text-sm font-medium text-stone-700">{t(f.label)}</label>
                  <FieldInput ws={ws} f={f} val={val} set={set} readOnly={readOnly} />
                </div>
              ))}
            </div>
          );

        // ---- checkboxes ----
        if (b.type === "checkboxes")
          return (
            <div key={bi}>
              <h4 className="mb-2 text-base font-semibold text-stone-800">{t(b.title)}</h4>
              <div className="flex flex-wrap gap-3">
                {b.items.map((it) => (
                  <label key={it.key} className="flex items-center gap-2 rounded-md border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-stone-800">
                    <input type="checkbox" disabled={readOnly} checked={!!val(it.key)}
                      data-testid={`${ws.id}-${it.key}`}
                      onChange={(e) => set(it.key, e.target.checked, true)} className="h-4 w-4 accent-emerald-700" />
                    {t(it.label)}
                  </label>
                ))}
              </div>
            </div>
          );

        // ---- rowtable ----
        if (b.type === "rowtable") {
          const rows = b.rowLabels || Array.from({ length: b.rows }, (_, i) => null);
          return (
            <div key={bi}>
              {b.title && <h4 className="mb-2 text-base font-semibold text-stone-800">{t(b.title)}</h4>}
              <div className="overflow-x-auto rounded-md border border-[#E5DFD3]">
                <table className="w-full min-w-[640px] border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#E8EFE9] text-left">
                      {b.rowLabels && <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-500"> </th>}
                      {b.columns.map((c) => (
                        <th key={c.key} className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-500">{t(c.label)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((rl, r) => (
                      <tr key={r} className="border-t border-[#EFE9DE]">
                        {b.rowLabels && <td className="px-3 py-2 font-medium text-stone-700">{rl ? t(rl) : ""}</td>}
                        {b.columns.map((c) => {
                          const key = `${b.key}_${r}_${c.key}`;
                          return (
                            <td key={c.key} className="px-2 py-1.5">
                              {c.type === "select" ? (
                                <select className={inputCls} value={val(key) || ""} disabled={readOnly}
                                  data-testid={`${ws.id}-${key}`}
                                  onChange={(e) => set(key, e.target.value, true)}>
                                  <option value="">—</option>
                                  {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                              ) : (
                                <GridText value={val(key)} disabled={readOnly}
                                  testid={`${ws.id}-${key}`}
                                  onChange={(v) => set(key, v)} />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }

        // ---- dashboard ----
        if (b.type === "dashboard")
          return <DashboardBlock key={bi} data={dashboardData} />;

        return null;
      })}
    </div>
  );
}

function FieldInput({ ws, f, val, set, readOnly }) {
  const t = f.inputType || f.type;
  if (t === "select")
    return (
      <select className={inputCls} disabled={readOnly} value={val(f.key) || ""}
        data-testid={`${ws.id}-${f.key}`} onChange={(e) => set(f.key, e.target.value, true)}>
        <option value="">—</option>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  return (
    <input type={t === "date" ? "date" : "text"} className={inputCls} disabled={readOnly}
      data-testid={`${ws.id}-${f.key}`} value={val(f.key) || ""}
      onChange={(e) => set(f.key, e.target.value, t === "date")} />
  );
}

// Dashboard block imports charts lazily via recharts
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";
import { DOMAIN_ROWS, STAGES } from "@/config/worksheets";
import { interpret } from "@/lib/status";

function DashboardBlock({ data }) {
  const { t } = useTranslation();
  if (!data) return <p className="text-sm text-stone-500">{t("Loading dashboard…")}</p>;
  const current = data.current || {};
  const byStage = data.by_stage || {};
  const radarData = DOMAIN_ROWS.filter((d) => current[d.wid] != null).map((d) => ({
    domain: d.label.split(" ")[0], score: current[d.wid],
  }));
  return (
    <div className="space-y-6" data-testid="dashboard-block">
      <div className="flex flex-wrap items-center gap-4">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-3">
          <div className="text-xs font-mono uppercase tracking-wider text-emerald-700">{t("Overall domain average")}</div>
          <div className="font-serif text-4xl font-bold text-emerald-900" data-testid="overall-score">{data.overall ?? "—"}</div>
        </div>
        <div className="text-xs text-stone-500">{t("Scores are auto-calculated from completed scored worksheets (0–10).")}</div>
      </div>

      {radarData.length >= 3 && (
        <div className="h-72 w-full rounded-lg border border-[#E5DFD3] bg-white p-2">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="#CAD2C5" />
              <PolarAngleAxis dataKey="domain" tick={{ fontSize: 11, fill: "#525A54" }} />
              <Radar dataKey="score" stroke="#2D6A4F" fill="#2D6A4F" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-[#E5DFD3]">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="bg-[#E8EFE9] text-left">
              <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-500">{t("Domain")}</th>
              {STAGES.map((s) => <th key={s} className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-500">{t(s)}</th>)}
              <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-500">{t("Interpretation")}</th>
            </tr>
          </thead>
          <tbody>
            {DOMAIN_ROWS.map((d) => {
              const cur = current[d.wid];
              const it = interpret(cur);
              return (
                <tr key={d.wid} className="border-t border-[#EFE9DE]" data-testid={`dash-row-${d.wid}`}>
                  <td className="px-3 py-2 font-medium text-stone-700">{t(d.label)}</td>
                  {STAGES.map((s) => (
                    <td key={s} className="px-3 py-2 font-mono" style={{ color: interpret(byStage[s]?.[d.wid]).color }}>
                      {byStage[s]?.[d.wid] ?? "—"}
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${it.bg} ${it.badge}`}>{t(it.label)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
